import { Box, BoxProps, Button, Input, onEnter, Tooltip } from '@ytl/common-web';
import { useCallback, useMemo, useState } from 'react';
import { ReactComponent as InfoIcon } from '../../../assets/info.svg';
import { useLanguage } from '../../../contexts/languageContext';

export const useMfaEnterAuthenticatorNameView = (onContinue: (name: string) => Promise<void>, onBack: () => void) => {
  const { translation } = useLanguage();
  const [name, setName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const continueClicked = useCallback(async () => {
    if (!name) {
      return;
    }

    try {
      setIsLoading(true);
      await onContinue(name);
    } catch (err) {
      setError(translation.accountSettings.mfa.authenticatorSetup.error);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, name, onContinue, translation]);

  const boxConfig: Omit<BoxProps, 'children'> = useMemo(() => {
    return {
      header: translation.accountSettings.mfa.authenticatorSetup.title,
      subHeader: translation.accountSettings.mfa.authenticatorSetup.content,
      action: (
        <Button
          disabled={isLoading}
          onClick={() => continueClicked()}
        >
          {translation.accountSettings.mfa.authenticatorSetup.continue}
        </Button>
      ),
      subAction: (
        <Button
          variant="text-undecorated"
          onClick={() => onBack()}
          disabled={isLoading}
        >
          {translation.accountSettings.mfa.authenticatorSetup.cancel}
        </Button>
      ),
    };
  }, [translation, onBack, continueClicked, isLoading]);

  const view = useMemo(
    () => (
      <Box {...boxConfig}>
        <Input
          label={
            <>
              {translation.accountSettings.mfa.authenticatorSetup.nameLabel}
              <Tooltip
                text={translation.accountSettings.mfa.authenticatorSetup.nameLabelTooltip}
                icon={<InfoIcon className={'icon icon--tooltip'} />}
                includeIconInTooltip={true}
              />
            </>
          }
          aria-label={translation.accountSettings.mfa.authenticatorSetup.nameLabel}
          name={'name'}
          type={'text'}
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={isLoading}
          errorList={error ? [error] : []}
          onKeyUp={onEnter(continueClicked, () => setError(''))}
        />
      </Box>
    ),
    [boxConfig, translation, name, isLoading, error, continueClicked],
  );

  return {
    view,
  };
};
